import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Anchor, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JuniorLogin() {
  const nav = useNavigate();
  const [pin, setPin] = useState('');
  const [identifier, setIdentifier] = useState('Ashairah');
  const [busy, setBusy] = useState(false);

  const tap = (d) => {
    if (pin.length < 4) setPin(pin + d);
  };
  const back = () => setPin(pin.slice(0, -1));
  const clear = () => setPin('');

  const submit = async () => {
    if (pin.length < 4) {
      toast.error('Your PIN is 4 numbers, Captain.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/junior/login`, { identifier, pin }, { withCredentials: true });
      localStorage.setItem('sov_junior_token', data.token);
      localStorage.setItem('sov_junior_name', data.junior.name);
      toast.success(`Welcome aboard, ${data.junior.full_name || data.junior.name}!`);
      nav('/junior/command');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wrong PIN. Try again, Captain.');
      setPin('');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      {/* Cyber starfield backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-10 left-12 w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
        <div className="absolute top-32 right-24 w-1 h-1 bg-[#00E5FF] rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-20 left-40 w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 right-12 w-1 h-1 bg-[#00E5FF] rounded-full animate-pulse" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-1/2 left-1/3 w-1 h-1 bg-[#D4AF37] rounded-full animate-pulse" style={{ animationDelay: '0.8s' }} />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-[#D4AF37] bg-gradient-to-br from-[#D4AF37]/20 to-[#00E5FF]/10 mb-6">
            <Anchor size={36} strokeWidth={1.5} className="text-[#D4AF37]" />
          </div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#00E5FF] mb-3">CyberKids · Junior Command</div>
          <h1 className="text-4xl md:text-5xl font-extralight tracking-tight">
            Hello, <span className="font-display italic text-[#D4AF37]">Junior Admiral</span>
          </h1>
          <p className="text-[#A3A3A3] text-sm mt-4 font-light">Tap your secret PIN to enter your vault.</p>
        </div>

        {/* Name selector (single junior for now, but flexible) */}
        <div className="sov-border bg-[#0A0A0A] p-6 mb-6">
          <label className="block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">Captain's Name or Email</label>
          <input
            data-testid="junior-name-input"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="Ashairah  /  ashairah777@gmail.com"
            className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-2 text-lg font-light tracking-wide"
          />
        </div>

        {/* PIN display */}
        <div data-testid="junior-pin-display" className="flex justify-center gap-4 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-14 h-14 border-2 flex items-center justify-center text-3xl font-display ${
                pin.length > i ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#2a2a2a] text-[#444]'
              } transition-colors`}
            >
              {pin.length > i ? '*' : ''}
            </div>
          ))}
        </div>

        {/* Number pad */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <button
              key={d}
              data-testid={`junior-pad-${d}`}
              onClick={() => tap(String(d))}
              className="aspect-square sov-border bg-[#0A0A0A] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] text-3xl font-display text-[#F5F5F5] transition-all active:scale-95"
            >
              {d}
            </button>
          ))}
          <button
            data-testid="junior-pad-clear"
            onClick={clear}
            className="aspect-square border border-[#444] bg-[#0A0A0A] hover:border-[#888] text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] transition-all active:scale-95"
          >
            Clear
          </button>
          <button
            data-testid="junior-pad-0"
            onClick={() => tap('0')}
            className="aspect-square sov-border bg-[#0A0A0A] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] text-3xl font-display text-[#F5F5F5] transition-all active:scale-95"
          >
            0
          </button>
          <button
            data-testid="junior-pad-back"
            onClick={back}
            className="aspect-square border border-[#444] bg-[#0A0A0A] hover:border-[#888] text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] transition-all active:scale-95"
          >
            ←
          </button>
        </div>

        <button
          data-testid="junior-login-submit"
          onClick={submit}
          disabled={busy || pin.length < 4}
          className="w-full py-4 bg-[#D4AF37] text-[#050505] font-medium text-sm tracking-[0.3em] uppercase hover:bg-[#E5C158] disabled:opacity-30 transition-colors flex items-center justify-center gap-3"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} strokeWidth={2} />}
          Board My Ship
        </button>

        <p className="text-[10px] tracking-[0.25em] uppercase text-center text-[#666] mt-6">
          Hint to Mum & Dad: Default PIN is 1234 · Rotate from the Admiral console.
        </p>
      </div>
    </div>
  );
}
